import { useState, useEffect } from 'react';
import { Users, Settings, LogOut, Wrench, LayoutDashboard, Sparkles, Receipt, BarChart3, FileText, Undo2, Landmark, CalendarDays, LifeBuoy } from 'lucide-react';
import { useVertical } from '../../context/VerticalContext';
import { useRole } from '../../context/RoleContext';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationBell } from '../ui/NotificationBell';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { reportError } from '../../lib/errors';

export const Sidebar = () => {
    const { molded } = useVertical();
    const { can, role } = useRole();
    const { pendingCreditsCount, expensesReviewCount } = useNotifications();
    const { jargon } = molded;
    const location = useLocation();
    const navigate = useNavigate();
    const activePath = location.pathname;

    // Org name state — from cache first, then DB
    const [orgName, setOrgName] = useState<string>(() => {
        return localStorage.getItem('fds_org_name_cache') || '';
    });

    useEffect(() => {
        const fetchOrgName = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;
            const { data, error: fetchError } = await supabase
                .from('profiles')
                .select('active_org_id')
                .eq('id', user.id)
                .single();
            if (fetchError) reportError(fetchError, 'sidebar.profiles');
            if (data?.active_org_id) {
                const { data: org, error: orgError } = await supabase
                    .from('organizations')
                    .select('name')
                    .eq('id', data.active_org_id)
                    .single();
                if (orgError) reportError(orgError, 'sidebar.organizations');
                if (org?.name) {
                    setOrgName(org.name);
                    localStorage.setItem('fds_org_name_cache', org.name);
                }
            }
        };
        fetchOrgName();
    }, []);

    if (!jargon.sidebar) return null;

    // Readable role label
    const roleLabel = role === 'owner' ? 'Owner' : role === 'admin' ? 'Accountant' : role === 'mechanic' ? 'Mechanic' : role === 'member' ? 'Member' : '';

    // Icon Map for dynamic rendering
    const iconMap: Record<string, any> = {
        LayoutDashboard,
        Wrench,
        Users,
        FileText,
        Receipt,
        BarChart3,
        Sparkles,
        Settings,
        Undo2,
        Landmark,
        CalendarDays
    };

    const sidebarConfig = molded.sidebar_config || [];

    // Schedule (full calendar) — promoted out of the Workshop tabs to its own
    // top-level page. Injected after Dashboard so it sits near the top; gated by
    // the same workshop permission as jobs.
    const withSchedule = [...sidebarConfig];
    if (!withSchedule.some((i) => i.path === '/schedule')) {
        const dashIdx = withSchedule.findIndex((i) => i.path === '/');
        withSchedule.splice(dashIdx >= 0 ? dashIdx + 1 : 0, 0, {
            label: 'Schedule',
            path: '/schedule',
            icon: 'CalendarDays',
            permission: 'workshop',
        } as (typeof sidebarConfig)[number]);
    }

    const visibleNavItems = withSchedule.filter((item) => {
        return !item.permission || can(item.permission as any);
    }).map(item => ({
        ...item,
        icon: iconMap[item.icon] || LayoutDashboard,
        active: item.path === '/'
            ? activePath === '/'
            : activePath.startsWith(item.path)
    }));

    return (
        <aside data-theme="dark" className="hidden lg:flex flex-col w-[220px] h-screen fixed left-0 top-0 bg-sunken border-r border-[var(--fd-line)] shadow-[1px_0_0_var(--fd-scrim)] z-50 print:hidden no-print">
            {/* ── Brand ── */}
            <div className="px-6 pt-6 pb-5">
                <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
                    <img src="/logo.webp" alt="" className="h-8 w-auto" />
                    <span className="text-[19px] font-semibold tracking-[-0.03em] font-display text-fg">
                        ForgeDash
                    </span>
                </Link>
                <p className="fd-eyebrow mt-4">
                    {molded.jargon.trade_label} OS
                </p>
            </div>

            {/* ── Nav ── */}
            <nav className="flex-1 px-3 space-y-0.5 mt-1 overflow-y-auto">
                {visibleNavItems.map((item) => (
                    <Link
                        key={item.label}
                        to={item.path}
                        className={`
                            relative flex items-center gap-3 pl-4 pr-3 h-[44px] rounded-[var(--fd-r-md)]
                            transition-colors duration-150 group
                            ${item.active
                                ? 'fd-nav-active text-fg'
                                : 'text-fg-2 hover:text-fg hover:bg-card'
                            }
                        `}
                    >
                        {/* 3px gold active rail */}
                        {item.active && (
                            <span className="absolute left-0 top-1/2 -translate-y-1/2 h-[22px] w-[3px] rounded-r bg-gold-fill" />
                        )}

                        <item.icon
                            strokeWidth={2}
                            className={`w-[18px] h-[18px] shrink-0 transition-colors ${item.active ? 'text-gold' : 'text-fg-3 group-hover:text-fg'}`}
                        />
                        <span className={`flex-1 min-w-0 truncate text-sm ${item.active ? 'font-semibold' : 'font-medium'}`}>
                            {item.label}
                        </span>

                        {item.path === '/returns' && pendingCreditsCount > 0 && (
                            <span className="ml-auto shrink-0 bg-danger-fill text-fg py-0.5 px-2 rounded-full text-fd-3xs font-bold font-mono">
                                {pendingCreditsCount > 99 ? '99+' : pendingCreditsCount}
                            </span>
                        )}
                        {item.path === '/expenses' && expensesReviewCount > 0 && (
                            <span className="ml-auto bg-danger-fill w-2 h-2 rounded-full" />
                        )}
                    </Link>
                ))}

                {/* MOT reminders moved into the Clients screen (an "MOT Reminders"
                    card there opens the upcoming-MOT list). No standalone sidebar
                    entry — it's part of the customer/vehicle record now. */}
            </nav>

            {/* ── Footer: help · garage · role · online, bell, sign out ── */}
            <div className="px-3 pb-4 pt-3 border-t border-[var(--fd-line)] space-y-0.5">
                {/* Chrome rather than navigation — deliberately not in
                    sidebar_config, so no vertical can reorder it away. */}
                <Link
                    to="/help"
                    className={`flex items-center gap-3 px-3 h-[44px] w-full rounded-[var(--fd-r-md)] transition-colors ${activePath.startsWith('/help')
                        ? 'text-fg bg-raised'
                        : 'text-fg-3 hover:text-fg hover:bg-card'
                        }`}
                >
                    <LifeBuoy strokeWidth={2} className={`w-4 h-4 ${activePath.startsWith('/help') ? 'text-gold' : ''}`} />
                    <span className="text-fd-sm-tight">Help &amp; Guides</span>
                </Link>

                {orgName && (
                    <div className="flex items-center gap-2.5 px-3 py-2.5 mt-1 rounded-[var(--fd-r-md)] bg-card border border-line">
                        <span className="w-2 h-2 rounded-full bg-success shrink-0" title="Online" />
                        <span className="flex-1 min-w-0">
                            <span className="block text-fd-sm-tight font-semibold font-display text-fg truncate" title={orgName}>
                                {orgName}
                            </span>
                            {roleLabel && (
                                <span className="block text-fd-3xs uppercase tracking-[0.14em] font-medium text-fg-4">
                                    {roleLabel}
                                </span>
                            )}
                        </span>
                        <NotificationBell />
                    </div>
                )}

                <button
                    onClick={async () => {
                        await supabase.auth.signOut();
                        navigate('/');
                    }}
                    className="flex items-center gap-3 px-3 h-[44px] w-full rounded-[var(--fd-r-md)] text-fg-3 hover:text-danger hover:bg-card transition-colors group"
                >
                    <LogOut strokeWidth={2} className="w-4 h-4 transition-colors" />
                    <span className="text-fd-sm-tight">Sign out</span>
                </button>
            </div>
        </aside>
    );
};
